import React, { useRef, useEffect } from "react";
import { Star, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import FileUploader from "@/components/admin/FileUploader";

/** Manages an ordered list of `{url, alt:{en,es,ru}, isMain, order}`. */
export default function PhotoManager({ images = [], onChange, testid = "photo-manager" }) {
  // Ref tracks the latest images so sequential uploads (multi-file) chain off
  // the freshest list rather than the stale prop captured at upload start.
  const ref = useRef(images);
  useEffect(() => {
    ref.current = images;
  }, [images]);

  const commit = (next) => {
    const cleaned = next.map((img, i) => ({ ...img, order: i }));
    if (cleaned.length && !cleaned.some((i) => i.isMain)) cleaned[0].isMain = true;
    ref.current = cleaned;
    onChange(cleaned);
  };

  const addUploaded = (res) => {
    const prev = ref.current || [];
    commit([
      ...prev,
      { url: res.url, alt: { en: "", es: "", ru: "" }, isMain: prev.length === 0, order: prev.length },
    ]);
  };

  const remove = (i) => commit(ref.current.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const arr = [...ref.current];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    commit(arr);
  };
  const makeMain = (i) => commit(ref.current.map((img, idx) => ({ ...img, isMain: idx === i })));

  return (
    <div data-testid={testid}>
      <FileUploader accept="image/*" multiple onUploaded={addUploaded} testid={`${testid}-uploader`} label="Drop / click to upload images" />

      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
          {images.map((img, i) => (
            <div key={`${img.url}-${i}`} className="border border-[#2A2A2A] bg-[#0C0C0C]" data-testid={`${testid}-item-${i}`}>
              <div className="relative aspect-[4/3] bg-[#1a1a1a]">
                <img src={img.url} alt="" className="w-full h-full object-cover" />
                {img.isMain && (
                  <div className="absolute top-1.5 left-1.5 text-[10px] tracking-[0.16em] uppercase bg-[#C9A55A] text-[#0C0C0C] px-2 py-1 font-semibold">Main</div>
                )}
              </div>
              <div className="flex items-center justify-between p-2 gap-1">
                <button onClick={() => makeMain(i)} disabled={img.isMain} title="Make main"
                  data-testid={`${testid}-main-${i}`}
                  className="p-2 border border-[#2A2A2A] text-[#F5F1EB] hover:border-[#C9A55A] hover:text-[#C9A55A] disabled:opacity-40">
                  <Star size={12} fill={img.isMain ? "#C9A55A" : "none"} />
                </button>
                <button onClick={() => move(i, -1)} disabled={i === 0} title="Move up"
                  data-testid={`${testid}-up-${i}`}
                  className="p-2 border border-[#2A2A2A] text-[#F5F1EB] hover:border-[#C9A55A] hover:text-[#C9A55A] disabled:opacity-40">
                  <ArrowUp size={12} />
                </button>
                <button onClick={() => move(i, 1)} disabled={i === images.length - 1} title="Move down"
                  data-testid={`${testid}-down-${i}`}
                  className="p-2 border border-[#2A2A2A] text-[#F5F1EB] hover:border-[#C9A55A] hover:text-[#C9A55A] disabled:opacity-40">
                  <ArrowDown size={12} />
                </button>
                <button onClick={() => remove(i)} title="Delete"
                  data-testid={`${testid}-del-${i}`}
                  className="p-2 border border-[#2A2A2A] text-[#F5F1EB] hover:border-[#B8342A] hover:text-[#B8342A]">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
